import React, { useEffect, useState } from "react";
import { t as T } from "i18next";
import { Link } from "react-router-dom";
import { Flame, BadgeCheck, Users } from "lucide-react";
import api from "../../lib/api";

/**
 * CreatorRail — høyre-side rail på desktop (lg:+). På mobil skjules via BetaForside.
 * Viser 4 betalte creator-slots. Boosted først, random fyller.
 * Random bestemmes ved page load (ingen auto-rotasjon).
 */
export default function CreatorRail() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/feed/creator-rail")
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || slots.length === 0) return null;

  return (
    <aside className="sticky top-4 space-y-3" data-testid="creator-rail">
      <div className="font-meta text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-1.5"
           style={{ background: "linear-gradient(90deg,#ff2b8a,#ffd145)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
        <Flame className="h-3 w-3 text-[var(--magenta)]" strokeWidth={2.2} />
        {T("ui.boosted_creators")}
      </div>
      {slots.map((s) => (
        <Link
          key={s.id}
          to={s.href}
          className="block relative overflow-hidden rounded-md transition-transform hover:scale-[1.02]"
          style={{
            background: "#141116",
            border: s.is_boosted
              ? "2px solid rgba(255, 95, 168, 0.9)"
              : "2px solid rgba(255, 95, 168, 0.5)",
            boxShadow: s.is_boosted
              ? "0 0 24px rgba(255, 95, 168, 0.35), inset 0 0 12px rgba(255, 95, 168, 0.15)"
              : "0 0 12px rgba(255, 95, 168, 0.15)",
          }}
          data-testid={`creator-rail-slot-${s.username}`}
        >
          {/* Badges over bildet */}
          <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between pointer-events-none">
            <span className="font-meta text-[8px] uppercase tracking-widest bg-black/70 text-[var(--magenta)] px-1.5 py-0.5 rounded">
              CREATOR
            </span>
            {s.is_boosted ? (
              <span className="font-meta text-[8px] uppercase tracking-widest bg-black/70 text-[var(--gold)] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <Flame className="h-2.5 w-2.5" strokeWidth={2.5} /> BOOSTED
              </span>
            ) : s.is_featured ? (
              <span className="font-meta text-[8px] uppercase tracking-widest bg-black/70 text-[var(--gold)] px-1.5 py-0.5 rounded">
                FREMHEVET
              </span>
            ) : null}
          </div>

          {/* Bilde */}
          <div className="aspect-[3/4] bg-black/60 overflow-hidden">
            {s.avatar_url ? (
              <img src={s.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-fraunces italic text-5xl text-white/20">
                  {(s.username || "?").slice(0, 2).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Navn + meta */}
          <div className="p-3 bg-[#141116]">
            <div className="font-fraunces italic text-sm text-[var(--cream)] truncate">
              {s.display_name}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[10px] font-meta text-[var(--cream-faint)]">
              {s.is_verified && <BadgeCheck className="h-3 w-3 text-[var(--magenta)]" strokeWidth={2.4} />}
              <Users className="h-2.5 w-2.5" />
              <span>{s.follower_count} følgere</span>
            </div>
            {s.region && (
              <div className="font-meta text-[9px] uppercase tracking-wider text-[var(--cream-faint)] mt-1">
                🇳🇴 {s.region}
              </div>
            )}
          </div>
        </Link>
      ))}
    </aside>
  );
}
